import React, { useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';

interface MetricSparklineProps {
  label: string;
  points: number[];
  unit?: string;
  colorClass?: string;
  className?: string;
}

const MetricSparkline: React.FC<MetricSparklineProps> = ({
  label,
  points,
  unit = '%',
  colorClass = 'text-emerald-500',
  className,
}) => {
  const { colors } = useTheme();

  const { path, latest, min, max } = useMemo(() => {
    if (points.length === 0) {
      return { path: '', latest: null as number | null, min: 0, max: 100 };
    }
    const minV = Math.min(...points);
    const maxV = Math.max(...points);
    const range = maxV - minV || 1;
    const w = 200;
    const h = 48;
    const polylinePoints = points
      .map((v, i) => {
        const x = points.length === 1 ? w / 2 : (i / (points.length - 1)) * w;
        const y = h - ((v - minV) / range) * (h - 8) - 4;
        return `${x},${y}`;
      })
      .join(' ');
    return {
      path: polylinePoints,
      latest: points[points.length - 1],
      min: minV,
      max: maxV,
    };
  }, [points]);

  return (
    <div className={cn('rounded-lg border p-3 font-sans', colors.border, colors.headerBg, className)}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className={cn('text-[10px] uppercase tracking-wider font-semibold', colors.textMuted)}>{label}</p>
        {latest != null && (
          <p className={cn('text-sm font-bold tabular-nums', colors.text)}>
            {latest}
            {unit}
          </p>
        )}
      </div>
      {points.length < 2 ? (
        <p className={cn('text-xs h-12 flex items-center font-sans', colors.textMuted)}>Collecting samples…</p>
      ) : (
        <svg viewBox="0 0 200 48" className="w-full h-12" preserveAspectRatio="none" aria-hidden>
          <polyline
            fill="none"
            className={cn(colorClass, 'opacity-90')}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={path}
          />
        </svg>
      )}
      {points.length >= 2 && (
        <p className={cn('text-[10px] mt-1 tabular-nums', colors.textMuted)}>
          Range {min}
          {unit} – {max}
          {unit} · {points.length} samples
        </p>
      )}
    </div>
  );
};

export default MetricSparkline;
